// ============================================================================
// CrewAhead Maritime Platform - Vessel Lookup API Functions
// ============================================================================
// Vessel lookup by IMO/MMSI/name, search history, stats, and PSC reports.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Vessel {
  imo: string;
  mmsi: string;
  name: string;
  callsign: string | null;
  vesselType: string | null;
  grossTonnage: number | null;
  deadweight: number | null;
  yearBuilt: number | null;
  flag: string | null;
  lengthOverall: number | null;
  beam: number | null;
  status: string | null;
  owner: string | null;
  manager: string | null;
  photoUrl: string | null;
  cachedAt: string | null;
}

export interface VesselLookupResult {
  vessel: Vessel;
  cached: boolean;
  cachedAt: string | null;
  source: string;
}

export interface VesselSearchByNameResult {
  count: number;
  results: Vessel[];
  source: string;
  cached: boolean;
}

export interface PSCReport {
  id: string;
  inspectionDate: string;
  authority: string;
  port: string;
  country: string;
  detention: boolean;
  deficiencies: number;
  reportUrl: string | null;
  createdAt: string;
}

export interface PSCReportsResponse {
  imo: string;
  vesselName: string;
  totalReports: number;
  detentions: number;
  reports: PSCReport[];
}

export interface VesselSearchLog {
  id: string;
  searchType: string;
  searchQuery: string;
  imoFound: string;
  vesselName: string;
  found: boolean;
  sourceUsed: string;
  createdAt: string;
}

export interface VesselSearchHistoryResponse {
  count: number;
  results: VesselSearchLog[];
}

export interface VesselStats {
  totalLookups: number;
  todayLookups: number;
  foundRate: number;
  topSearches: Array<{
    searchQuery: string;
    searchType: string;
    count: number;
  }>;
  uniqueVesselsCached: number;
  pscReportsTotal: number;
}

// ─── Lookup ──────────────────────────────────────────────────────────────────

/**
 * GET /vessels/lookup/?imo={imo}
 *
 * Lookup a vessel by IMO number.
 *
 * @param imo - IMO vessel identifier (7 digits)
 * @returns Vessel lookup result with cache info
 */
export async function lookupVesselByIMO(imo: string): Promise<VesselLookupResult> {
  try {
    const response = await apiClient.get<VesselLookupResult>(
      `${API_ENDPOINTS.VESSEL.LOOKUP}?imo=${encodeURIComponent(imo)}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /vessels/lookup/?mmsi={mmsi}
 *
 * Lookup a vessel by MMSI number.
 *
 * @param mmsi - MMSI vessel identifier (9 digits)
 * @returns Vessel lookup result with cache info
 */
export async function lookupVesselByMMSI(mmsi: string): Promise<VesselLookupResult> {
  try {
    const response = await apiClient.get<VesselLookupResult>(
      `${API_ENDPOINTS.VESSEL.LOOKUP}?mmsi=${encodeURIComponent(mmsi)}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /vessels/lookup/?name={name}
 *
 * Search vessels by name (requires at least 3 characters).
 *
 * @param name - Vessel name to search
 * @returns List of matching vessels
 */
export async function searchVesselsByName(
  name: string
): Promise<VesselSearchByNameResult> {
  try {
    const response = await apiClient.get<VesselSearchByNameResult>(
      `${API_ENDPOINTS.VESSEL.LOOKUP}?name=${encodeURIComponent(name)}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Detail ──────────────────────────────────────────────────────────────────

/**
 * GET /vessels/{imo}/
 *
 * Get vessel detail by IMO.
 *
 * @param imo - IMO vessel identifier
 * @returns Vessel lookup result
 */
export async function getVessel(imo: string): Promise<VesselLookupResult> {
  try {
    const response = await apiClient.get<VesselLookupResult>(
      API_ENDPOINTS.VESSEL.DETAIL(imo)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── PSC Reports ─────────────────────────────────────────────────────────────

/**
 * GET /vessels/{imo}/psc/
 *
 * Get PSC (Port State Control) reports for a vessel.
 *
 * @param imo - IMO vessel identifier
 * @returns PSC reports with summary statistics
 */
export async function getPSCReports(imo: string): Promise<PSCReportsResponse> {
  try {
    const response = await apiClient.get<PSCReportsResponse>(
      API_ENDPOINTS.VESSEL.PSC_REPORTS(imo)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /vessels/{imo}/psc/{reportId}/
 *
 * Get a single PSC report detail.
 *
 * @param imo - IMO vessel identifier
 * @param reportId - PSC report ID
 * @returns PSC report detail
 */
export async function getPSCReport(
  imo: string,
  reportId: string
): Promise<PSCReport> {
  try {
    const response = await apiClient.get<PSCReport>(
      API_ENDPOINTS.VESSEL.PSC_REPORT_DETAIL(imo, reportId)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Search History ──────────────────────────────────────────────────────────

/**
 * GET /vessels/history/
 *
 * Get the authenticated user's vessel search history.
 * Authentication required.
 *
 * @returns Recent search history (up to 50 entries)
 */
export async function getVesselSearchHistory(): Promise<VesselSearchHistoryResponse> {
  try {
    const response = await apiClient.get<VesselSearchHistoryResponse>(
      API_ENDPOINTS.VESSEL.HISTORY
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Stats ───────────────────────────────────────────────────────────────────

/**
 * GET /vessels/stats/
 *
 * Admin-only: vessel lookup statistics.
 *
 * @returns Vessel lookup statistics
 */
export async function getVesselStats(): Promise<VesselStats> {
  try {
    const response = await apiClient.get<VesselStats>(
      API_ENDPOINTS.VESSEL.STATS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
